import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import type { Cliente } from '@/api/tienda.api';
import { DataTable, type Columna } from '@/components/datos/DataTable';
import { ConDetalle } from '@/components/datos/DetailDrawer';
import { EntityLink } from '@/components/datos/EntityLink';
import { useAbrirDetalle } from '@/components/datos/contexto-detalle';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { opcionesDe, sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { useToast } from '@/hooks/useToast';
import { numero, fecha } from '@/lib/formato-tienda';

/**
 * CLIENTAS — la libreta que Maciel ya tiene, pero que hoy vive en su cabeza.
 *
 * ═══ POR QUÉ NO ES LA PANTALLA «CLIENTES» ═══
 *
 * Hay otra pantalla llamada Clientes en el sistema y NO es esta: aquella arma
 * la ficha con órdenes de trabajo y despachos, que es el mundo de servicio en
 * campo de ELECTRO MEDIA. Una tienda de ropa no tiene técnicos. Son dos
 * modelos de datos distintos y por eso son dos pantallas distintas, cada una
 * colgada de su módulo.
 *
 * ═══ EL TELÉFONO ES LA IDENTIDAD ═══
 *
 * El agente da de alta a la clienta desde el chat con el número que le escribe,
 * y el número se normaliza antes de comparar: `8095551234` y `18095551234` son
 * la misma persona. Sin eso, la misma clienta entraría dos veces y su historial
 * quedaría partido en dos — que es justo lo que hace inútil una libreta.
 */

const VISTAS = [
  { id: 'todas', etiqueta: 'Todas' },
  { id: 'con-telefono', etiqueta: 'Con WhatsApp', ayuda: 'A las que el agente puede escribir' },
  { id: 'sin-direccion', etiqueta: 'Sin dirección', ayuda: 'No se les puede despachar' },
] as const;

const ORIGENES = [
  { valor: 'chat', etiqueta: 'La dio de alta el chat' },
  { valor: 'a-mano', etiqueta: 'Cargada a mano' },
] as const;

export default function Clientas() {
  return (
    <ConDetalle>
      <Contenido />
    </ConDetalle>
  );
}
function Contenido() {
  const abrir = useAbrirDetalle();
  const { filtros, poner, ponerVarios } = useFiltrosEnUrl(
    { vista: 'todas', buscar: '', zona: '', origen: '' },
    { vista: VISTAS.map((v) => v.id), origen: ORIGENES.map((o) => o.valor) },
  );
  const [editando, setEditando] = useState<Cliente | null>(null);
  const [nueva, setNueva] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['tienda', 'clientes'],
    queryFn: () => tienda.listarClientes({ limit: 200 }),
  });
  const clientas = useMemo(() => data ?? [], [data]);

  const conTelefono = clientas.filter((c) => !!c.telefono);
  const sinDireccion = clientas.filter((c) => !c.direccion);

  const filtradas = useMemo(() => {
    let salida = clientas;
    if (filtros.vista === 'con-telefono') salida = salida.filter((c) => !!c.telefono);
    if (filtros.vista === 'sin-direccion') salida = salida.filter((c) => !c.direccion);
    if (filtros.zona) salida = salida.filter((c) => (c.zona ?? '') === filtros.zona);
    if (filtros.origen) salida = salida.filter((c) => (filtros.origen === 'chat') === Boolean(c.contactId));

    const q = filtros.buscar.trim().toLowerCase();
    if (q) {
      salida = salida.filter((c) =>
        `${c.nombre} ${c.telefono ?? ''} ${c.zona ?? ''} ${c.direccion ?? ''}`
          .toLowerCase()
          .includes(q),
      );
    }
    return salida;
  }, [clientas, filtros.vista, filtros.buscar, filtros.zona, filtros.origen]);

  // SUPERFILTRO (Josué, 14-oct): las pestañas de antes son la faceta «Cuáles»; se suman zona y origen.
  const facetas: Faceta[] = [
    { clave: 'vista', titulo: 'Cuáles', todas: 'Todas', opciones: VISTAS.filter((v) => v.id !== 'todas').map((v) => ({ valor: v.id, etiqueta: v.etiqueta, cuenta: v.id === 'con-telefono' ? conTelefono.length : sinDireccion.length })) },
    { clave: 'zona', titulo: 'Zona', todas: 'Todas', opciones: opcionesDe(clientas, (c) => c.zona) },
    { clave: 'origen', titulo: 'Origen', opciones: ORIGENES.map((o) => ({ ...o, cuenta: clientas.filter((c) => (o.valor === 'chat') === Boolean(c.contactId)).length })) },
  ];

  const columnas: Columna<Cliente>[] = [
    {
      id: 'nombre',
      titulo: 'Nombre',
      fija: true,
      valor: (c) => c.nombre,
      celda: (c) => (
        <EntityLink
          tipo="cliente"
          id={c.id}
          etiqueta={c.nombre}
          detalle={c.zona ?? undefined}
          sinIcono
        />
      ),
    },
    {
      id: 'telefono',
      titulo: 'WhatsApp',
      valor: (c) => c.telefono ?? '',
      celda: (c) =>
        c.telefono ? (
          <span className="font-mono text-sm text-slate-700 dark:text-slate-200">{c.telefono}</span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      id: 'zona',
      titulo: 'Zona',
      valor: (c) => c.zona ?? '',
      celda: (c) => (c.zona ? <Badge>{c.zona}</Badge> : <span className="text-slate-400">—</span>),
    },
    {
      id: 'direccion',
      titulo: 'Dirección',
      valor: (c) => c.direccion ?? '',
      celda: (c) =>
        c.direccion ? (
          <span className="truncate text-slate-600 dark:text-slate-300">{c.direccion}</span>
        ) : (
          <span className="text-amber-600 dark:text-amber-500">sin dirección</span>
        ),
    },
    {
      id: 'origen',
      titulo: 'Origen',
      ocultaPorDefecto: true,
      valor: (c) => (c.contactId ? 'chat' : 'a mano'),
      celda: (c) =>
        c.contactId ? (
          <Badge variant="info" title="La dio de alta el agente desde el chat">
            Del chat
          </Badge>
        ) : (
          <span className="text-slate-500">A mano</span>
        ),
    },
    {
      id: 'alta',
      titulo: 'Desde',
      valor: (c) => c.createdAt,
      celda: (c) => <span className="text-slate-600 dark:text-slate-300">{fecha(c.createdAt)}</span>,
    },
  ];

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Clientela"
        subtitulo="Quién te compra, cuánto lleva y qué le queda pendiente. El agente las da de alta solo."
        acciones={
          <Button onClick={() => setNueva(true)}>
            <Plus className="h-4 w-4" />
            Nueva clienta
          </Button>
        }
        kpis={
          <>
            <Kpi titulo="Clientela" valor={numero(clientas.length)} onClick={() => poner('vista', 'todas')} />
            <Kpi
              titulo="Con WhatsApp"
              valor={numero(conTelefono.length)}
              pie="a las que se les puede escribir"
              onClick={() => poner('vista', 'con-telefono')}
            />
            <Kpi
              titulo="Sin dirección"
              valor={numero(sinDireccion.length)}
              pie="no se les puede despachar"
              tono={sinDireccion.length > 0 ? 'malo' : 'normal'}
              bajarEsBueno
              onClick={() => poner('vista', 'sin-direccion')}
            />
            <Kpi
              titulo="Dadas de alta por el agente"
              valor={numero(clientas.filter((c) => c.contactId).length)}
              pie="sin que nadie las escribiera"
            />
          </>
        }
      />


      <DataTable
        filas={filtradas}
        columnas={columnas}
        idDe={(c) => c.id}
        cargando={isLoading}
        error={isError ? (error instanceof Error ? error.message : 'Error') : null}
        onReintentar={() => void refetch()}
        buscar={filtros.buscar}
        onBuscar={(v) => poner('buscar', v)}
        placeholderBuscar="Busca por nombre, número o zona…"
        facetas={facetas}
        valoresDeFacetas={{ ...filtros, vista: filtros.vista === 'todas' ? undefined : filtros.vista }}
        onFacetas={ponerVarios}
        onLimpiarFiltros={() => ponerVarios(sinFiltros(facetas))}
        resultados={textoDeResultados(filtradas.length, clientas.length, ['cliente', 'clientes'])}
        memoria="clientas"
        exportarComo="clientas"
        onAbrirFila={(c) =>
          abrir?.({ tipo: 'cliente', id: c.id, etiqueta: c.nombre, detalle: c.telefono ?? undefined })
        }
        accionesDeFila={[
          {
            etiqueta: 'Editar',
            icono: <Pencil className="h-3.5 w-3.5" />,
            onClick: (c) => setEditando(c),
          },
        ]}
        vacio={{
          titulo: filtros.buscar ? `Nada con "${filtros.buscar}"` : 'Todavía no hay clientas',
          descripcion:
            'Se dan de alta solas cuando el agente atiende un chat, o puedes cargarlas a mano.',
          accion: <Button onClick={() => setNueva(true)}>Nueva clienta</Button>,
        }}
      />

      {(nueva || editando) && (
        <FormularioDeClienta
          clienta={editando}
          onCerrar={() => {
            setNueva(false);
            setEditando(null);
          }}
        />
      )}
    </div>
  );
}

function FormularioDeClienta({
  clienta,
  onCerrar,
}: {
  clienta: Cliente | null;
  onCerrar: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [nombre, setNombre] = useState(clienta?.nombre ?? '');
  const [telefono, setTelefono] = useState(clienta?.telefono ?? '');
  const [direccion, setDireccion] = useState(clienta?.direccion ?? '');
  const [zona, setZona] = useState(clienta?.zona ?? '');
  const [notas, setNotas] = useState(clienta?.notas ?? '');
  const [documento, setDocumento] = useState(clienta?.documento ?? '');

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        nombre: nombre.trim(),
        telefono: telefono.trim() || null,
        direccion: direccion.trim() || null,
        zona: zona.trim() || null,
        notas: notas.trim() || null,
        documento: documento.trim() || null,
      };
      return clienta ? tienda.actualizarCliente(clienta.id, cuerpo) : tienda.crearCliente(cuerpo);
    },
    onSuccess: () => {
      toast.success(clienta ? 'Clienta actualizada' : 'Clienta creada');
      void qc.invalidateQueries({ queryKey: ['tienda'] });
      onCerrar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar'),
  });

  return (
    <Modal open onClose={onCerrar} title={clienta ? 'Editar clienta' : 'Nueva clienta'}>
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Nombre
          </label>
          <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ana Pérez" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            WhatsApp
          </label>
          <Input
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            placeholder="809 555 1234"
            className="font-mono"
          />
          <p className="mt-1 text-xs text-slate-500">
            Es con lo que el agente la reconoce en el chat. Da igual cómo lo escribas: se normaliza
            antes de comparar.
          </p>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            RNC o cédula <span className="font-normal text-slate-400">(opcional)</span>
          </label>
          <Input
            value={documento}
            onChange={(e) => setDocumento(e.target.value)}
            placeholder="9 u 11 dígitos"
            inputMode="numeric"
            className="font-mono"
          />
          <p className="mt-1 text-xs text-slate-500">Con él, su factura sale lista para crédito fiscal.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Zona
            </label>
            <Input value={zona} onChange={(e) => setZona(e.target.value)} placeholder="Naco" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Dirección
            </label>
            <Input
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
              placeholder="Calle 5 #23, apto 2B"
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Notas
          </label>
          <Textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={2}
            placeholder="Le gusta el M, paga por transferencia…"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => guardar.mutate()} disabled={!nombre.trim() || guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
